// src/pages/Quotation/components/CreateQuotationForm.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  DatePicker,
  Typography,
  Grid,
  Radio,
  Flex,
  Select,
  Checkbox,
  Drawer,
  Tooltip,
  Button,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

import InputField from "../../../components/Input/Input";
import dayjs from "dayjs";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import {
  fetchQuotationVoucherNumber,
  fetchSalesAccounts,
} from "../quotationThunk";
import {
  fetchCustomers,
  fetchDiscountAccs,
  fetchItems,
  fetchPreviousDue,
  fetchSingleInstitutionDetails,
  fetchTaxAccs,
} from "../../../store/commonThunks/lookups";
import {
  calculateTotals,
  resetQuotation,
  setCustomer,
  setDiscountAcc,
  setFooterValues,
  setProductImage,
  setTaxAcc,
  setTaxMode,
  addItemRow,
  setQuotationTagFieldId,
  setConversion,
  setConversionRate,
  updateItemRow,
  emptyItemCart,
} from "../quotationSlice";
import { useLocation, useNavigate } from "react-router-dom";
import {
  selectCustomers,
  selectDiscountAccs,
  selectInstitutionData,
  selectPreviousDue,
  selectTaxAccs,
} from "../../../store/commonSelectors/lookupsSelectors";
import { getCurrencyCodeWithName } from "../../../lib/currency";
import { convertCurrency } from "../../../utils";
import CustomerDrawer from "../../Customers/components/CustomerDrawer";
import {
  formatToCompanyTimezone,
  getYearFromCompanyDate,
} from "../../../lib/functions";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const CreateQuotationForm = ({ authInfo, form, institution }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const location = useLocation();
  const [isConvertCurrencyShow, SetIsConvertCurrencyShow] = useState(false);
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const {
    voucherNumber,
    entryDate,
    customer,
    taxMode,
    selectedSalesAcc,
    footerValues,
    conversionRate,
  } = useSelector((s) => s.quotation);
  console.log(taxMode, "taxMode123");

  const { isEditQuotationRecord, selectedQuotationRecord } = useSelector(
    (s) => s.quotationRecord
  ); //for updating

  const lookups = useSelector((s) => s.lookups);
  

  const customers = useSelector(selectCustomers);
  const previousDue = useSelector(selectPreviousDue);

  const currency = authInfo?.currency || "AED";

  const isFieldHidden = (fieldKey, pageKey = "quotation_entry") => {
    return authInfo?.visibilitytabs?.some(
      (item) => item.page_key === pageKey && item.field_key === fieldKey
    );
  };

  useEffect(() => {
    if (isEditQuotationRecord) return;
    let formatedDate = entryDate ? dayjs(entryDate).toISOString() : null;
    let companydate = formatToCompanyTimezone(formatedDate);
    const year = getYearFromCompanyDate(companydate);
    dispatch(fetchQuotationVoucherNumber(year));
  }, [entryDate]);

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchSalesAccounts());    
  }, [dispatch]);

  // Don't preserve state if not required on pageload
  useEffect(() => {
    return () => {
      // reset only if NOT preserving
      if (!location.state?.preserveQuotation) {
        dispatch(resetQuotation());
      }
    };
  }, [dispatch, location.state]);

  // Whenever voucher number or customer changes (including being cleared), form resets too
  useEffect(() => {
    if (!isEditQuotationRecord) {
      form.resetFields();
    }
  }, [voucherNumber, dispatch]);

  const tagOptions = (lookups.tagFields || []).map((tag) => ({
    label: tag.display_text,
    value: tag.id,
  }));

  const handleCustomerSelect = (cus) => {
    if (!cus) {
      dispatch(setCustomer(null));
      return;
    }
    if (cus === "add_new") {
      setCustomerDrawerOpen(true);
      return;
    }

    dispatch(setCustomer(cus));

    //Reset ONLY dependent redux state
    dispatch(
      setFooterValues({
        discountPercent: 0,
        taxPercent: 0,
        serviceCharge: 0,
        paidAmount: 0,
        dueAmount: 0,
        subTotal: 0,
        description: "",
        termsConditions: institution?.quotation_terms || "",
        paymentTerms: institution?.quotation_payment_terms || "",
        useDefaultTerms: true,
      })
    );

    dispatch(emptyItemCart());
    dispatch(
      setFooterValues({
        subtotal: "0.00",
        totalTax: "0.00",
        grandTotal: "0.00",
        total_discount: "0.00",
        paidAmount: 0,
        dueAmount: 0,
        serviceCharge: 0,
        discountPct: 0,
        taxPct: 0,
      })
    );

    // fetch previous due
    dispatch(fetchPreviousDue(cus.acc_id));
    dispatch(fetchItems(cus?.party_type || false));
    dispatch(fetchTaxAccs()).then((res) => {
      if (res.payload && res.payload.length > 0) {
        dispatch(setTaxAcc(res.payload[0])); // pick first as default
      }
    });
    dispatch(fetchDiscountAccs()).then((res) => {
      if (res.payload && res.payload.length > 0) {
        dispatch(setDiscountAcc(res.payload[0])); // pick first as default
      }
    });
  };

  return (
    <div>
       <Flex justify="space-between" align="center">
          <Title level={3} >
            Quotation Entry
          </Title>

          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Flex>
      <Form
        key={voucherNumber || "new"}
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1200px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item
              label="Search Customer"
              name="search_customer"
              rules={[{ required: true, message: "Select customer" }]}
            >
              <SearchListAutoComplete
                entityType="customer"
                placeholder="Customer"
                optionsList={(customers || []).map((c) => ({
                  sale_id: c.acc_id,
                  display_text: c.acc_name || c.institution_name,
                  raw: c,
                }))}
                value={
                  customer
                    ? {
                      sale_id: customer.acc_id,
                      display_text: customer.acc_name,
                      raw: customer,
                    }
                    : undefined
                }

                onSelect={(obj) =>
                  handleCustomerSelect(obj?.raw ? obj?.raw : obj)
                }
                showAddNew={true}
              />
            </Form.Item>

            <Form.Item label="Quotation No.">
              <InputField
                value={voucherNumber}
                data-testid="quotation-no-input"
                disabled
              />
            </Form.Item>

            <Form.Item label="Institution Name">
              <InputField value={customer?.institution_name || ""} disabled />
            </Form.Item>

            <Form.Item label="Contact No">
              <InputField value={customer?.contact_no || ""} disabled />
            </Form.Item>
            <Form.Item label="Tags">
              <Select
                mode="multiple"
                placeholder="Select Tags"
                style={{ width: "100%" }}
                options={tagOptions}
                value={useSelector((s) => s.quotation.tagFieldId)}
                onChange={(selectedIds) =>
                  dispatch(setQuotationTagFieldId(selectedIds))
                }
              />
            </Form.Item>
          </Flex>

          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item label="Address">
              <InputField value={customer?.address || ""} disabled />
            </Form.Item>
            <Form.Item label="Entry Date">
              <DatePicker
                value={entryDate ? dayjs(entryDate) : null}
                disabled
                style={{ width: "100%" }}
                format="DD-MM-YYYY"
              />
            </Form.Item>
            <Form.Item label="Previous Due">
              <InputField
                value={Number(previousDue || 0).toFixed(2)}
                disabled
              />
            </Form.Item>

              <Form.Item label="Discount">
                <Radio.Group
                  options={[
                    { label: "On Total", value: "on_total" },
                    { label: "On Individual Item", value: "on_item" },
                  ]}
                  onChange={(e) => {
                    dispatch(setTaxMode(e.target.value));
                    dispatch(calculateTotals({ authInfo }));
                  }}
                  value={taxMode}
                />
              </Form.Item>
            {!isFieldHidden("Convert Currency") && (
              <Form.Item>
                <Checkbox
                    onChange={(e) => {
                    const checked = e.target.checked;

                    SetIsConvertCurrencyShow(checked);

                    if (!checked) {
                      // 🔥 Clear conversion from Redux
                      dispatch(setConversionRate(0));
                      dispatch(setConversion(null, "conversion_currency"));
                    }
                  }}
                >
                  Convert Currency
                </Checkbox>
              </Form.Item>
            )}
          </Flex>
        </Flex>
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          {isConvertCurrencyShow && (
            <>
              <Flex
                vertical
                gap={10}
                style={{
                  width: !screens.md || !screens.xl ? "100%" : "40%",
                }}
              >
                <Form.Item label="Choose Currency">
                  <Select
                    showSearch
                    placeholder="Select Currency"
                    style={{ minWidth: 200 }}
                    options={getCurrencyCodeWithName().map((c) => ({
                      value: c.code,
                      label: `${c.currency} (${c.code})`,
                    }))}
                    onChange={async (toCurrency) => {
                      const rate = await convertCurrency(currency, toCurrency);
                      if (rate) {
                        dispatch(
                          setConversion(toCurrency, "conversion_currency")
                        );
                        dispatch(setConversionRate(rate));
                      }
                    }}
                  />
                </Form.Item>
              </Flex>
              <Flex
                vertical
                gap={10}
                style={{
                  width: !screens.md || !screens.xl ? "100%" : "40%",
                }}
              >
                <Form.Item label="Conversion Rate">
                  <InputField value={conversionRate} disabled />
                </Form.Item>
              </Flex>
            </>
          )}
        </Flex>
      </Form>

      <CustomerDrawer
        open={customerDrawerOpen}
        onClose={() => setCustomerDrawerOpen(false)}
        authInfo={authInfo}
        onCreated={(customer) => {
          if (!customer) return;

          // 1️⃣ Build EXACT SearchListAutoComplete value shape
          const customerObj = {
            sale_id: customer.acc_id,
            display_text: customer.acc_name || customer.institution_name,
            raw: customer,
          };

          // 2️⃣ Close drawer
          setCustomerDrawerOpen(false);

          dispatch(fetchCustomers());
          // 3️⃣ Update Redux
          dispatch(setCustomer(customer));

          // 4️⃣ Set Form value (🔥 THIS WAS MISSING)
          form.setFieldValue("search_customer", customerObj);

          // 5️⃣ Trigger dependent flows
          dispatch(fetchItems(customer?.party_type || false));
          dispatch(fetchPreviousDue(customer.acc_id));

          dispatch(fetchTaxAccs()).then((res) => {
            if (res.payload?.length) {
              dispatch(setTaxAcc(res.payload[0]));
            }
          });

          dispatch(fetchDiscountAccs()).then((res) => {
            if (res.payload?.length) {
              dispatch(setDiscountAcc(res.payload[0]));
            }
          });
        }}
      />
      <Drawer
        title="Quotation Entry Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Quotation Entry
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            A quotation entry in the ERP system is used to create and manage
            customer quotations before a sale is finalized. It acts as a formal
            proposal containing product details, pricing, discounts, taxes,
            and customer information.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Steps to Create Quotation</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Open <strong>Quotation Entry</strong></li>
              <li>Select or create the customer</li>
              <li>Verify quotation number and entry date</li>
              <li>Add products/items with quantity and pricing</li>
              <li>Apply discounts if required</li>
              <li>Add tags or additional notes if needed</li>
              <li>Review quotation totals and details</li>
              <li>Click <strong>Save</strong> to create the quotation</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Customer selection is required before saving.</li>
              <li>Quotation totals change based on discounts and VAT settings.</li>
              <li>Currency conversion can be enabled if required.</li>
              <li>Saved quotations can later be converted into sales orders.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default CreateQuotationForm;
